<template>
	<div class="InputSelect" :class="classes">
		<label :for="id">{{ label }}</label>
		<select class="InputSelect-select" :disabled="disabled" :name="name" :id="id" :value="value" @change="emitEvent">
			<option v-if="disabledHeader" value="" selected disabled>{{ disabledHeader }}</option>
			<option v-for="(item, index) in options" :value="item.value" :selected="item.value === value" :key="index">
				{{ item.label }}
			</option>
		</select>
	</div>
</template>

<script>
export default {
	emits: ['change', 'update:value'],
	model: {
		prop: 'value',
		event: 'change',
	},
	props: {
		id: {
			type: String,
			default: 'id-' + Math.random().toString(36).substr(2, 9),
		},
		label: {
			type: String,
			required: true,
		},
		/**
		 * Array of objects like { value: 'a', label: 'Option A' }
		 */
		options: {
			required: true,
			type: Array,
		},
		value: {
			type: String | Number,
			default: null,
		},
		name: {
			type: String,
			default: null,
		},
		size: {
			type: String,
			default: null,
		},
		reverse: {
			type: Boolean,
			default: false,
		},
		disabled: {
			type: Boolean,
			default: false,
		},
		disabledHeader: {
			type: String,
			default: null,
		},
	},
	computed: {
		classes: function () {
			let classes = [];
			const prefix = 'InputSelect--';
			if (this.size) {
				classes.push(prefix + this.size);
			}
			if (this.reverse) {
				classes.push(prefix + 'reverse');
			}
			return classes.join(' ');
		},
	},
	data() {
		return {};
	},
	methods: {
		emitEvent(e) {
			this.$emit('change', e.target.value); // for use with v-model in Vue 2
			this.$emit('update:value', e.target.value); // for use with v-model in Vue 3
		},
	},
};
</script>

<style lang="scss">
.InputSelect {
	@include stack-md;
	.InputSelect-select {
		-moz-appearance: none;
		-webkit-appearance: none;
		appearance: none;
		background-color: $color-white; // needed only for Firefox
		background-repeat: no-repeat;
		background-position: right center;
		[dir='rtl'] & {
			background-position: left center;
		}
		background-size: 1.25rem 1.25rem;
		background-origin: content-box;
		// our triangleDown icon, optimized, with #858585 fill, and URL encoded
		background-image: url('data:image/svg+xml,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20viewBox%3D%220%200%2024%2024%22%3E%0A%20%20%3Cpath%20fill%3D%22%23858585%22%20d%3D%22M17.778%208.846a.736.736%200%200%201%200%201.054l-5.255%205.255a.736.736%200%200%201-1.054%200L6.214%209.9a.752.752%200%200%201%20.526-1.277h10.509c.2-.002.39.079.529.223z%22%2F%3E%0A%3C%2Fsvg%3E');
		height: auto;
		@include text(4);
		margin: 0;
		@include inset-sm;
		border: 1px solid $color-form-outline;
		border-radius: $radius-sm;
		&:focus {
			outline: 2px solid $color-form-outline-focus;
		}
		&:-moz-focusring {
			color: transparent;
			text-shadow: 0 0 0 $color-black;
		}
		&:disabled {
			background-color: $color-neutral-1;
		}
	}
	.InputSelect-errorMessage {
		display: none;
		@include text(3);
		color: $color-text-error;
	}
	label {
		@include text(3);
		@include stack-xs;
	}
	&--small,
	&--sm {
		@include stack-sm;
		.InputSelect-select {
			@include text(3);
			@include inset-xs;
		}
		label {
			@include text(2);
		}
		.InputSelect-errorMessage {
			@include text(2);
		}
	}
	&--large,
	&--lg {
		.InputSelect-select {
			@include text(5);
			@include inset-md;
		}
		label {
			@include text(3);
		}
	}
}

.InputSelect--compact {
	.InputSelect-select {
		margin-bottom: 0;
		@include breakpoint(small) {
			margin-bottom: 0.5rem;
		}
	}
}

.InputSelect--flex {
	flex: 1 0 100%;
	@include breakpoint(small) {
		flex: 1 0 0;
	}
}

.InputSelect--error {
	label {
		color: $color-text-error;
	}
	.InputSelect-field {
		border-color: $color-text-error;
	}
	.InputSelect-errorMessage {
		display: block;
	}
}

// only used to match style of readonly inputs, does not prevent editing. use disabled on options instead
.InputSelect--readonly {
	.InputSelect-select {
		background-color: $color-neutral-0;
	}
}

// want to use a placeholder?
// make the first option disabled, and apply this class
// (a better way is to use :invalid, but that only works when the select is required)
.InputSelect--disabledSelected {
	color: $color-neutral-4;
}

.InputSelect--reverse {
	.InputSelect-select {
		background-color: $color-black;
		border-color: $color-form-outline-reverse;
		// !important is needed for firefox
		color: $color-text-reverse !important;
		&:focus {
			outline-color: $color-form-outline-focus-reverse;
		}
		&:disabled {
			background-color: $color-neutral-5;
		}
	}
	option {
		// needed for firefox
		color: $color-text-reverse;
	}
	label {
		color: $color-text-reverse;
	}
	&.InputSelect--disabledSelected {
		.InputSelect-select {
			color: $color-text-muted-reverse;
		}
	}
}
</style>
